/**
 * Help context: the state the in-app help framework shares across the console.
 * Provided once by HelpProvider (mounted at app level).
 */

import { createContext, useContext } from 'react';
import type { HelpAppPreferenceView, HelpAskIntent, HelpAskResult, HelpPageView, HelpProgressView, HelpTourView } from '@actschurch/ccms-sdk';

/** One question and its answer in the Ask AI panel. */
export interface HelpAskEntry {
  id: number;
  /** What the person asked: the typed question or the chip label. */
  label: string;
  pageKey: string;
  tabKey: string | null;
  status: 'loading' | 'done' | 'error';
  result?: HelpAskResult;
  error?: string;
}

export interface HelpAskInput {
  /** The typed or picked question. Empty when a quick action (intent) is sent. */
  question?: string;
  intent?: HelpAskIntent;
  /** Shown in the conversation as what was asked. */
  label: string;
  /** Defaults to the page in the help panel, then the page on screen. */
  pageKey?: string | null;
  /** Defaults to the tab open on that page. */
  tabKey?: string | null;
  /** Open the help panel (e.g. when asked from a tab popover). */
  openPanel?: boolean;
}

export interface ActiveTourRun {
  runId: number;
  tour: HelpTourView;
  /** Admin preview: never records progress. */
  preview: boolean;
  /**
   * Opened by itself on a first visit: abandoned quietly when the person leaves the page first, and a Skip
   * switches off auto-start for every tour of its app.
   */
  auto: boolean;
  /** The app the tour belongs to (the page's appKey), for the per-app auto-start setting. */
  appKey: string | null;
  /** How long the first step waits for its target before falling back to a centred card. */
  firstWaitMs: number;
  /** Pause after the first target appears (lets the page settle). */
  startDelayMs: number;
  /** Path when the run was requested (auto runs only start on this path). */
  startPath: string;
}

export interface StartTourOptions {
  /** Page to fetch the tour from when it is not cached yet. */
  pageKey?: string | null;
  preview?: boolean;
  auto?: boolean;
  /** The tour's app; defaults to the cached page's appKey, then the app part of the tour's pageKey. */
  appKey?: string | null;
  firstWaitMs?: number;
  startDelayMs?: number;
}

/** How a tour ended without being finished: a link in a step was followed (not the Skip button, X or Esc). */
export interface FinishTourOptions {
  navigated?: boolean;
}

export interface HelpDrawerState {
  opened: boolean;
  pageKey: string | null;
  tabKey: string | null;
  /** Bumped to move focus to the Ask AI box. */
  focusAskNonce: number;
}

export interface HelpContextValue {
  /** Signed in: help can be loaded. */
  ready: boolean;
  /** Admin role or the Help.Content.Manage right. */
  canManageHelp: boolean;
  /** Admin role (may open AI feature flags). */
  isAdmin: boolean;
  /** Tour progress by tourKey: the server's merged with this device's unsent changes. */
  progress: Map<string, HelpProgressView>;
  progressLoaded: boolean;
  /** Per-app help settings by appKey (server merged with this device's unsent changes; missing = defaults). */
  appPreferences: Map<string, HelpAppPreferenceView>;
  preferencesLoaded: boolean;
  /** Whether the app's tours may open by themselves for this person (default true). */
  appAutoStart: (appKey: string) => boolean;
  /**
   * Saves the app's auto-start setting (this device first, then the server, retried until it is saved).
   * Turning it on makes the open page's tour eligible from the next time the page is opened.
   */
  setAppAutoStart: (appKey: string, autoStartTours: boolean, options?: { pageKey?: string | null }) => void;
  ask: { enabled: boolean; message: string | null; loaded: boolean };

  /** The page on screen (registered by its PageHelpButton). */
  currentPageKey: string | null;
  registerPage: (pageKey: string) => () => void;
  activeTabFor: (pageKey: string | null | undefined) => string | null;
  setActiveTab: (pageKey: string, tabKey: string | null) => void;

  drawer: HelpDrawerState;
  openHelp: (pageKey?: string | null, tabKey?: string | null, options?: { focusAsk?: boolean }) => void;
  closeHelp: () => void;

  activeTour: ActiveTourRun | null;
  startTour: (tour: HelpTourView | string, options?: StartTourOptions) => Promise<boolean>;
  /** Navigate to a route, then start the tour once the page's anchors exist. */
  showMeHow: (route: string, tourKey: string, pageKey?: string | null) => void;
  /** Done records Completed; Skip records Dismissed (and, for a tour that opened by itself, switches off its app's tours). */
  finishTour: (outcome: 'completed' | 'dismissed', options?: FinishTourOptions) => void;
  abandonTour: () => void;
  autoStartIfDue: (page: HelpPageView) => void;
  resetToursForPage: (page: HelpPageView) => Promise<void>;
  tourStatus: (tour: HelpTourView) => 'new' | 'completed' | 'dismissed' | 'updated';

  conversation: HelpAskEntry[];
  askAi: (input: HelpAskInput) => void;
  clearConversation: () => void;
}

export const HelpContext = createContext<HelpContextValue | null>(null);

/** The help context, or null outside HelpProvider (help UI then renders nothing). */
export function useHelpContext(): HelpContextValue | null {
  return useContext(HelpContext);
}
